import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthTokenService } from 'src/app/shared/services/auth-token.service';
import { FusionAuthService } from 'src/app/shared/services/fusion-auth.service';
import { isInvalidUserError } from 'src/app/shared/types/inactive-user-error.type';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root',
})

/**
 * Handle token refresh for outgoing requests. The authentication token expires every 18 days (at time of writing this),
 * but we plan to shorten this soon.
 */
export class TokenRefreshInterceptor implements HttpInterceptor {
  constructor(
    private auth: AuthService,
    private authTokenService: AuthTokenService,
    private fusionAuthService: FusionAuthService,
  ) {}

  /**
   * This function will check the token expiry.
   *
   * @param request - Represents the request on its way to the server. We check the token expiry to identify this outgoing request requires authorization before sending the request to the server.
   * @param next - Represents next step in the interceptor chain.
   * @returns - Observable that wraps up an HttpEvent object.
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // we only want to check token expiry IF the request we are sending out requires authorization. We'll confirm that
    // by checking for the header
    if (this.auth.isUserLoggedIn() && request.headers.has('Authorization')) {
      // this is a request that requires auth,
      return from(next.handle(request)).pipe(
        catchError((error: unknown) => {
          console.error('API request failed', error);
          const errorResponse = error as HttpErrorResponse;
          // Ignore 401 for the inactive user.
          const isUserInactiveError = isInvalidUserError(error) && error.error.error === 'User not active';
          if (errorResponse?.status === 401 && !isUserInactiveError) {
            return from(this.refreshAccessTokenAndUpdateRequest(request)).pipe(
              switchMap((newRequest) => (newRequest ? next.handle(newRequest) : EMPTY)),
            );
          }

          return throwError(() => error);
        }),
      );
    }
    return next.handle(request);
  }

  /**
   * Common method to update the request with the new access token.
   *
   * @param request - current HTTP request
   * @returns New request with the updates access token
   */
  async refreshAccessTokenAndUpdateRequest(request: HttpRequest<unknown>): Promise<any> {
    // token has expired, so attempt to refresh
    try {
      await this.fusionAuthService.handleTokenRefresh();
      const newRequest = request.clone({
        headers: request.headers.set('Authorization', `Bearer ${this.authTokenService.accessToken}`),
      });
      return newRequest;
    } catch (error) {
      console.error('Session expired. Please login to continue.', error);
      await this.auth.logout();
    }
  }
}
